"use client";

import { Menu, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { DocsSidebar, type DocsSidebarItem } from "./DocsSidebar";

interface DocsLayoutProps {
  children: React.ReactNode;
  sidebar: DocsSidebarItem[];
  rightSidebar?: React.ReactNode;
  tabs?: { id: string; label: string; disabled?: boolean }[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

export function DocsLayout({
  children,
  sidebar,
  rightSidebar,
  tabs,
  activeTab,
  onTabChange,
}: DocsLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bg">
      {/* Mobile overlay - must be outside flex container for proper stacking */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 top-[64px] md:top-[72px] z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu toggle button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed top-[68px] md:top-[76px] right-4 z-[60] lg:hidden p-2 rounded-lg bg-bg-card border border-border shadow-sm"
        aria-label={isMobileMenuOpen ? "Close navigation" : "Open navigation"}
      >
        {isMobileMenuOpen ? (
          <X className="w-5 h-5 text-text" />
        ) : (
          <Menu className="w-5 h-5 text-text" />
        )}
      </button>

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed top-[64px] md:top-[72px] left-0 z-50 w-[280px] bg-bg-card border-r border-border transition-transform duration-200 ease-out lg:hidden",
          "h-[calc(100vh-64px)] md:h-[calc(100vh-72px)] overflow-y-auto",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Tabs (optional) */}
        {tabs && tabs.length > 0 && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && onTabChange?.(tab.id)}
                disabled={tab.disabled}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                  tab.disabled
                    ? "text-text-muted/50 cursor-not-allowed"
                    : activeTab === tab.id
                      ? "text-primary bg-primary/5"
                      : "text-text-muted hover:text-text hover:bg-bg-alt"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
        <DocsSidebar items={sidebar} />
      </aside>

      <div className="relative z-0 max-w-[1600px] mx-auto px-4 lg:px-6">
        <div className="flex">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block sticky top-[72px] w-[280px] shrink-0 h-fit max-h-[calc(100vh-72px)] overflow-y-auto">
            {/* Tabs (optional) */}
            {tabs && tabs.length > 0 && (
              <div className="flex items-center gap-1 px-4 pt-6 pb-2 border-b border-border">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => !tab.disabled && onTabChange?.(tab.id)}
                    disabled={tab.disabled}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                      tab.disabled
                        ? "text-text-muted/50 cursor-not-allowed"
                        : activeTab === tab.id
                          ? "text-primary bg-primary/5"
                          : "text-text-muted hover:text-text hover:bg-bg-alt"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            <DocsSidebar items={sidebar} />
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 px-4 lg:px-12 py-8 lg:py-12">
            <div className="max-w-3xl">{children}</div>
          </main>

          {/* Right Sidebar */}
          {rightSidebar && (
            <aside className="hidden xl:block w-[220px] shrink-0 sticky top-[72px] h-fit max-h-[calc(100vh-72px)] overflow-y-auto py-8">
              {rightSidebar}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
